import React, { useMemo, useState } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';

/* ----------------------------- Types ----------------------------- */
type Mode = 'AIR' | 'SURFACE';
type Payment = 'PPD' | 'COD';
type PLType = 'PROFIT' | 'LOSS';

type RecoBill = {
  id: string;
  crn: string;
  date: string;
  courier: string;
  trackingNo: string;
  orderId: string;
  mode: Mode;
  payment: Payment;
  zone: string;
  shippingCourier: number;
  shippingOur: number;
  weightCourier: number;
  weightOur: number;
  slab: string;
  pl: number;
};

/* ----------------------------- Mock Data ----------------------------- */
const DATA: RecoBill[] = [
  {
    id: '1',
    crn: 'CRN123456',
    date: '12 Jan 2026',
    courier: 'Delhivery',
    trackingNo: 'DLV12345',
    orderId: 'ORD9981',
    mode: 'AIR',
    payment: 'PPD',
    zone: 'Zone-1',
    shippingCourier: 120,
    shippingOur: 100,
    weightCourier: 1.2,
    weightOur: 1.0,
    slab: '0.5–1kg',
    pl: 20,
  },
  {
    id: '2',
    crn: 'CRN654321',
    date: '13 Jan 2026',
    courier: 'Bluedart',
    trackingNo: 'BLD54321',
    orderId: 'ORD9982',
    mode: 'SURFACE',
    payment: 'COD',
    zone: 'Zone-2',
    shippingCourier: 150,
    shippingOur: 180,
    weightCourier: 2.1,
    weightOur: 2.0,
    slab: '2–2.5kg',
    pl: -30,
  },
  {
    id: '3',
    crn: 'CRN777888',
    date: '14 Jan 2026',
    courier: 'Xpressbees',
    trackingNo: 'XPB77889',
    orderId: 'ORD9983',
    mode: 'AIR',
    payment: 'COD',
    zone: 'Zone-3',
    shippingCourier: 200,
    shippingOur: 170,
    weightCourier: 3.0,
    weightOur: 2.5,
    slab: '2.5–3kg',
    pl: 30,
  },
];

/* ----------------------------- Component ----------------------------- */
export default function RecoBillsAdminPage() {
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState<'ALL' | Mode>('ALL');
  const [paymentFilter, setPaymentFilter] = useState<'ALL' | Payment>('ALL');
  const [plFilter, setPlFilter] = useState<'ALL' | PLType>('ALL');

  const filteredData = useMemo(() => {
    return DATA.filter((row) => {
      if (
        search &&
        !(
          row.crn.includes(search) ||
          row.trackingNo.includes(search) ||
          row.orderId.toLowerCase().includes(search.toLowerCase())
        )
      ) {
        return false;
      }

      if (modeFilter !== 'ALL' && row.mode !== modeFilter) return false;
      if (paymentFilter !== 'ALL' && row.payment !== paymentFilter)
        return false;
      if (plFilter === 'PROFIT' && row.pl <= 0) return false;
      if (plFilter === 'LOSS' && row.pl >= 0) return false;

      return true;
    });
  }, [search, modeFilter, paymentFilter, plFilter]);

  const FILTERS = [
    {
      label: 'Mode',
      value: modeFilter,
      set: setModeFilter,
      options: [
        { label: 'All modes', value: 'ALL' },
        { label: 'Air', value: 'AIR' },
        { label: 'Surface', value: 'SURFACE' },
      ],
    },
    {
      label: 'Payment',
      value: paymentFilter,
      set: setPaymentFilter,
      options: [
        { label: 'All payments', value: 'ALL' },
        { label: 'PPD', value: 'PPD' },
        { label: 'COD', value: 'COD' },
      ],
    },
    {
      label: 'P&L',
      value: plFilter,
      set: setPlFilter,
      options: [
        { label: 'All P&L', value: 'ALL' },
        { label: 'Profit', value: 'PROFIT' },
        { label: 'Loss', value: 'LOSS' },
      ],
    },
  ];

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Reco Bills</h1>
        <p className="text-text1 mt-1">
          Review courier reconciliation, charges & profit/loss
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex flex-1 gap-4 min-w-[220px] max-w-xl">
          <div className="w-full">
            <Input
              placeholder="Search by CRN / Tracking / Order ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              startEnhancer={<Search size={18} />}
            />
          </div>
          {(search || modeFilter !== 'ALL' || paymentFilter !== 'ALL' || plFilter !== 'ALL') && (
            <Button
              variant="outline"
              onClick={() => {
                setSearch('');
                setModeFilter('ALL');
                setPaymentFilter('ALL');
                setPlFilter('ALL');
              }}
              className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
            >
              Reset Filters
            </Button>
          )}
        </div>

        {FILTERS.map((f) => (
          <Select
            key={f.label}
            className="w-40"
            value={f.value}
            onChange={(e) => f.set(e.target.value as any)}
            placeholder={f.options[0]?.label}
            options={f.options.map((o) => ({ label: o.label, value: o.value }))}
          />
        ))}
      </div>

      {/* Table */}
      {/* Table Section */}
      <div className="overflow-x-auto">
        <div className="space-y-3">
          {/* Detached Teal Table Header Card */}
          <div className="bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
            <div className="grid [grid-template-columns:1fr_80px_1fr_1.5fr_1fr_100px_repeat(5,1fr)] gap-2 xl:gap-4 text-center items-center">
              <div>Date</div>
              <div>CRN</div>
              <div>Courier</div>
              <div>Tracking / Order</div>
              <div>Mode</div>
              <div>Payment</div>
              <div>Zone</div>
              <div>Shipping (C / O)</div>
              <div>Weight (C / O)</div>
              <div>Slab</div>
              <div>P&L</div>
            </div>
          </div>

          {/* Reco Bill Row Cards */}
          {filteredData.length === 0 ? (
            <div className="bg-white border rounded-xl py-20 text-center text-text1 shadow-sm">
              No reconciliation records found
            </div>
          ) : (
            filteredData.map((row) => {
              const isLoss = row.pl < 0;
              return (
                <div
                  key={row.id}
                  className="bg-card border rounded-xl px-3 xl:px-5 py-3 shadow-sm transition-hover hover:shadow-md"
                >
                  <div className="grid [grid-template-columns:1fr_80px_1fr_1.5fr_1fr_100px_repeat(5,1fr)] gap-2 xl:gap-4 text-xs xl:text-sm text-gray-700 items-start text-center">
                    {/* Date */}
                    <div className="font-medium text-text">{row.date}</div>

                    {/* CRN */}
                    <div className="font-bold text-blue-500 underline cursor-pointer">
                      {row.crn}
                    </div>

                    {/* Courier */}
                    <div className="font-medium">{row.courier}</div>

                    {/* Tracking / Order */}
                    <div className="space-y-1">
                      <div className="font-bold text-text underline">
                        {row.trackingNo}
                      </div>
                      <div className="text-[11px] text-text3 font-medium ">
                        {row.orderId}
                      </div>
                    </div>

                    {/* Mode */}
                    <div>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          row.mode === 'AIR'
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-brown-50 text-orange-700'
                        }`}
                      >
                        {row.mode}
                      </span>
                    </div>

                    {/* Payment */}
                    <div className="font-medium">{row.payment}</div>

                    {/* Zone */}
                    <div className="font-medium text-text3">{row.zone}</div>

                    {/* Shipping Comparison */}
                    <div className="space-y-1">
                      <div className="text-xs">
                        <span className="text-text3">C:</span> ₹
                        {row.shippingCourier}
                      </div>
                      <div className="text-xs font-bold text-teal">
                        <span className="text-text3">O:</span> ₹
                        {row.shippingOur}
                      </div>
                    </div>

                    {/* Weight Comparison */}
                    <div className="space-y-1">
                      <div className="text-xs">
                        <span className="text-text3">C:</span>{' '}
                        {row.weightCourier}kg
                      </div>
                      <div className="text-xs font-bold text-teal">
                        <span className="text-text3">O:</span> {row.weightOur}kg
                      </div>
                    </div>

                    {/* Slab */}
                    <div className="text-xs font-medium text-text3 bg-gray-50 py-1 rounded">
                      {row.slab}
                    </div>

                    {/* P&L */}
                    <div className="flex justify-center">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold border ${
                          isLoss
                            ? 'bg-red-50 text-red-600 border-red-100'
                            : 'bg-green-50 text-green-600 border-green-100'
                        }`}
                      >
                        {isLoss ? '-' : '+'}₹{Math.abs(row.pl)}
                      </span>
                    </div>
                  </div>

                  {/* Separator and Metadata Tags */}
                  <hr className="my-4 border-gray-100" />
                  <div className="flex justify-between items-center">
                    <div className="text-left">
                      <span className="inline-block bg-gray-100 text-gray-600 text-[11px] px-3 py-1 rounded-full">
                        Reco Summary: {row.courier} • {row.mode} • Weight Diff:{' '}
                        {(row.weightCourier - row.weightOur).toFixed(2)}kg
                      </span>
                    </div>
                    <div
                      className={`text-[11px] font-bold  tracking-widest ${isLoss ? 'text-red-500' : 'text-teal'}`}
                    >
                      Result: {isLoss ? 'Loss Incurred' : 'Profit Realized'}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {/* Bottom Padding */}
          <div className="h-20" />
        </div>
      </div>
    </div>
  );
}
